/** "WHERE ARE YOU PLAYING TONIGHT?" — the player's own venue choice.
 *
 *  Shown only to a player who holds badges at two or more venues that are
 *  playing tonight and has not yet been placed. One badge places them with no
 *  question asked; no badge means they can watch the board but not play.
 *
 *  The choice is final until the 03:00 reset. Only an owner, manager or super
 *  admin can lift it, from the Players box.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MapPin } from 'lucide-react';

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

export const GoalVenueChoice = ({ cardId, deviceId, skin = {}, onChosen }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!deviceId) return;
        try {
            const res = await axios.get(
                `${ORIGIN}/api/goal-game/public/${cardId}/venues?device_id=${encodeURIComponent(deviceId)}`);
            setData(res.data);
        } catch (_e) { /* no answer: the screen stays exactly as it was */ }
    }, [cardId, deviceId]);

    useEffect(() => { load(); }, [load]);

    const choose = async (venueId) => {
        setBusy(true);
        try {
            await axios.post(`${ORIGIN}/api/goal-game/public/${cardId}/venue`,
                             { device_id: deviceId, venue_id: venueId });
            await load();
            onChosen?.();
        } finally { setBusy(false); }
    };

    if (!data || !data.badges_enabled) return null;
    const accent = skin.accent || '#E11D48';
    const card = skin.card || '#1A202C';
    const border = skin.border || '#2D3748';

    // Nothing about venues is shown to a player who has only one of them —
    // which is every player at a single-venue business.
    if ((data.choices || []).length < 2 && !data.tonight) return null;
    if (data.tonight && (data.badges || []).length < 2) return null;
    if (data.tonight) {
        return (
            <p className="mb-3 flex items-center gap-2 rounded-xl border px-3 py-2 text-xs"
               style={{ borderColor: border, backgroundColor: card }}
               data-testid="goal-venue-locked">
                <MapPin size={12} />
                {t('goalGame.venueLocked', { venue: data.tonight.venue_name })}
            </p>
        );
    }

    // A BADGE NEVER STOPS ANYBODY PLAYING. A player with no badge, or with one
    // venue, is shown nothing at all — no wording, no choice. The "Where are
    // you playing tonight?" question is only for someone who really holds
    // badges at two or more venues playing tonight.
    if ((data.choices || []).length < 2) return null;

    return (
        <div className="mb-3 rounded-2xl border px-3 py-3"
             style={{ borderColor: border, backgroundColor: card }}
             data-testid="goal-venue-choice">
            <p className="text-sm font-bold">{t('goalGame.venueChoiceTitle')}</p>
            <p className="mb-2 text-xs opacity-70">{t('goalGame.venueChoiceHint')}</p>
            <div className="space-y-2">
                {data.choices.map((v) => (
                    <button key={v.venue_id} type="button" disabled={busy}
                            onClick={() => choose(v.venue_id)}
                            data-testid={`goal-venue-pick-${v.venue_id}`}
                            className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-xl border px-3 text-left text-sm font-semibold disabled:opacity-50"
                            style={{ borderColor: accent }}>
                        <span className="truncate">{v.venue_name}</span>
                        <span className="text-[11px] opacity-70">
                            {v.how === 'link' ? t('goalGame.howLink') : t('goalGame.howCard')}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default GoalVenueChoice;
